import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { environment } from '../environments/environment';
import {
  ChangePasswordPayload,
  ForgotPayload,
  LoginPayload,
  LoginResponse,
  ResetPasswordPayload,
  ResetPasswordResponse,
  VerifyOtpPayload,
  logoutResponse,
  verifyPayload
} from '../app/models/auth.model';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { CommonResponse } from '../app/models/commonResponse.model';
import { assets } from '../app/core/constants/assets.const';
import { PointerDetails } from '../app/models/sideBar';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isOpenSubject = new BehaviorSubject<boolean>(false);
  FCMtoken: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(
    null
  );
  resetTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  isOpen$ = this.isOpenSubject.asObservable();
  profileDetails: BehaviorSubject<LoginResponse | null> =
    new BehaviorSubject<LoginResponse | null>(null);

  emailSubject: BehaviorSubject<ForgotPayload | null> =
    new BehaviorSubject<ForgotPayload | null>(null);

  socketPointerSubject: BehaviorSubject<PointerDetails[] | null> = new BehaviorSubject<PointerDetails[] | null>(null);

  toggleSidebarView(): void {
    this.isOpenSubject.next(!this.isOpenSubject.value);
  }

  constructor(
    private readonly http: HttpClient,
    private _translateService: TranslateService
  ) {}

  isAuthenticated(): boolean {
    if (
      sessionStorage.getItem(environment.storageKey) &&
      sessionStorage.getItem(environment.storageKey) !== null
    ) {
      return true;
    } else {
      return false;
    }
  }

  logoutAlert(
    config = {
      title: 'AreYouSure',
      descrp: 'Youwillbeloggedout',
      icon: assets.logOutIcn,
      showCancelButton: true,
      showConfirmButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Logout'
    }
  ): Promise<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Promise((resolve: any) => {
      Swal.fire({
        title: this._translateService.instant(config.title),
        text: this._translateService.instant(config.descrp),
        imageUrl: config.icon,
        imageAlt: 'Icon',
        showCancelButton: config.showCancelButton,
        showConfirmButton:config.showConfirmButton,
        cancelButtonText: this._translateService.instant(config.cancelButtonText),
        confirmButtonText: this._translateService.instant(config.confirmButtonText),

        // 🔹 Add custom classes here
        customClass: {
          popup: 'swal-popup',
          title: 'swal-title',
          htmlContainer: 'swal-content',
          actions: 'swal-actions',
          confirmButton: 'swal-confirm',
          cancelButton: 'swal-cancel'
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }).then((result: any) => {
        return resolve(result);
      });
    });
  }

  fetchNotifications(query: string): Observable<any> {
    return this.http
      .get<{
        data: any;
      }>(`${environment.base_url}Coorporate/notification?${query}`)
      .pipe(map(res => res.data));
  }

  loginUser(loginDetails: LoginPayload): Observable<LoginResponse> {
    return this.http
      .post<{
        data: LoginResponse;
      }>(`${environment.base_url}User/login`, loginDetails)
      .pipe(map(res => res.data));
  }

  getProfile(query: string = ''): Observable<LoginResponse> {
    return this.http
      .get<{ data: LoginResponse }>(`${environment.base_url}User/getProfile${query}`)
      .pipe(map(res => res.data));
  }

  logout(): Observable<logoutResponse> {
    return this.http.get<logoutResponse>(`${environment.base_url}User/logout`);
  }

  forgotPassword(email: ForgotPayload): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}User/forgotPassword`,
      email
    );
  }

  verifyOtp(otp: VerifyOtpPayload): Observable<LoginResponse> {
    return this.http
      .post<{
        data: LoginResponse;
      }>(`${environment.base_url}User/verifyOtp`, otp)
      .pipe(map(res => res.data));
  }

  resetPassword(data: ResetPasswordPayload): Observable<ResetPasswordResponse> {
    return this.http
      .post<ResetPasswordResponse>(
        `${environment.base_url}User/resetPassword`,
        data
      )
      .pipe(map(res => res));
  }

  verify(payload: verifyPayload): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}User/sendOtp`,
      payload
    );
  }

  updateProfile(data: LoginResponse): Observable<LoginResponse> {
    return this.http
      .put<{
        data: LoginResponse;
      }>(`${environment.base_url}User/updateProfile`, data)
      .pipe(map(res => res.data));
  }

  changePassword(data: ChangePasswordPayload): Observable<CommonResponse> {
    return this.http
      .post<CommonResponse>(`${environment.base_url}User/changePassword`, data)
      .pipe(map(res => res));
  }

  setProfile(profile: LoginResponse): void {
    this.profileDetails.next(profile);
  }

  getPointerSocketDetails(): Observable<PointerDetails[]> {
    return this.http
      .get<{ data: PointerDetails[]}>(`${environment.payment_url}Order/index-count`)
      .pipe(map(res => res.data));
  }

  decreaseSideBarIndex(data: { entityId?: string | string[]; entityIds?: string[]; isMarkAll?: boolean; type?: string; bookingType?: number; otherPanels?: string }): Observable<CommonResponse> {
    return this.http
      .post<CommonResponse>(`${environment.payment_url}Order/index-count-seen`, data);
  }

  loadProfile(): Promise<void> {
    return new Promise(resolve => {
      if (!this.isAuthenticated()) {
        resolve();
        return;
      }

      this.getProfile().subscribe({
        next: profile => {
          this.profileDetails.next(profile);
          resolve();
        },
        error: () => resolve()
      });
    });
  }
}
